import React, { useState, useEffect } from 'react';
import Navbar from '../MainComponents/Navbar';
import Footer from '../MainComponents/Footer';
import './Orders.css';
import { FaLocationDot, FaChevronRight, FaArrowRight, FaBurger, FaChevronLeft } from 'react-icons/fa6';
import { FaPizzaSlice, FaClock, FaBoxOpen, FaCheckCircle, FaTimesCircle, FaCheck } from 'react-icons/fa';
import { BiSolidSushi } from 'react-icons/bi';
import { MdRamenDining } from 'react-icons/md';
import { IoLeaf } from 'react-icons/io5';
import { GiCupcake, GiKnifeFork } from 'react-icons/gi';
import { BiSolidCoffeeBean } from 'react-icons/bi';
import { Link, useLocation } from 'react-router-dom';
import Card from '../Home/Card';
import RatingModal from './RatingModal';
import { useTranslation } from 'react-i18next';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  where,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../Firebaseconfig';

/* ── Category icon pill ── */
function CategoryPill({ icon, label, active, onClick }) {
  return (
    <div className={`orders-cat-pill${active ? ' active' : ''}`} onClick={onClick}>
      <div className="orders-cat-icon">{icon}</div>
      <span>{label}</span>
    </div>
  );
}

/* ── Single order card (same visual style as Home product Card) ── */
function OrderCard({ order, onLeaveReview }) {
  const { t } = useTranslation();
  const STATUS_CONFIG = {
    pending: { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', icon: <FaClock />, label: t('orders.statusPending') },
    confirmed: { color: '#4ade80', bg: 'rgba(74,222,128,0.12)', icon: <FaCheckCircle />, label: t('orders.statusConfirmed') },
    delivered: { color: '#4ade80', bg: 'rgba(74,222,128,0.12)', icon: <FaCheckCircle />, label: t('orders.statusDelivered') },
    cancelled: { color: '#f87171', bg: 'rgba(248,113,113,0.12)', icon: <FaTimesCircle />, label: t('orders.statusCancelled') },
  };

  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;

  const formattedDate = order.createdAt?.toDate

    ? order.createdAt.toDate().toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    })
    : '—';

  const firstLetter = (order.companyName || order.productName || '?')[0].toUpperCase();

  return (
    <div className="order-card">
      {/* Image area */}
      <div className="order-card-image-container">
        <div className="order-card-badge" style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}33` }}>
          {cfg.icon} {cfg.label}
        </div>
        <div className="order-card-no-image">
          <GiKnifeFork style={{ fontSize: 36, color: '#2a2a2a' }} />
        </div>
      </div>

      {/* Content */}
      <div className="order-card-content">
        <div className="order-card-header">
          <h3 className="order-card-name">{order.productName || t('orders.defaultOrder')}</h3>
          <span className="order-card-price">${order.total != null ? Number(order.total).toFixed(2) : '0.00'}</span>
        </div>

        <p className="order-card-desc">
          {order.itemsText || `1x ${order.productName || t('orders.defaultItem')}`}
        </p>

        <div className="order-card-footer">
          <div className="order-card-poster">
            <div className="order-card-avatar">{firstLetter}</div>
            <span>{order.companyName || t('orders.defaultRestaurant')}</span>
          </div>
          <span className="order-card-date">{formattedDate}</span>
        </div>
        
        {order.status === 'delivered' && (
          <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <button 
              className="rm-btn-secondary" 
              style={{ width: '100%', borderColor: '#ff6b00', color: '#ff6b00' }}
              onClick={() => onLeaveReview && onLeaveReview(order)}
            >
              {t('rating.leaveReview') || "Leave a Review"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Orders() {
  const { t } = useTranslation();

  const CATEGORIES = [
    { label: t('home.exploreCategories').replace(/^\d+\.\s*/, '').replace('Explore ', 'All ') || 'All', value: 'All', icon: <FaBoxOpen /> },
    { label: t('home.catPizza'), value: 'Pizza', icon: <FaPizzaSlice /> },
    { label: t('home.catSushi'), value: 'Sushi', icon: <BiSolidSushi /> },
    { label: t('home.catBurgers'), value: 'Burgers', icon: <FaBurger /> },
    { label: t('home.catAsian'), value: 'Asian', icon: <MdRamenDining /> },
    { label: t('home.catHealthy'), value: 'Healthy', icon: <IoLeaf /> },
    { label: t('home.catDesserts'), value: 'Desserts', icon: <GiCupcake /> },
    { label: t('home.catCoffee'), value: 'Coffee', icon: <BiSolidCoffeeBean /> },
  ];

  const [currentUser, setCurrentUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [authLoading, setAuthLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('category') || 'All';
  });
  const [searchQuery, setSearchQuery] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('search') || '';
  });
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoaded, setIsLoaded] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [reviewingOrder, setReviewingOrder] = useState(null);

  /* Hero intro */
  useEffect(() => { setIsLoaded(true); }, []);

  /* URL Search & Category Sync */
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const search = params.get('search');
    if (search !== null) {
      setSearchQuery(search);
    } else {
      setSearchQuery('');
    }

    const cat = params.get('category');
    if (cat !== null) {
      const match = CATEGORIES.find(c => c.value.toLowerCase() === cat.toLowerCase());
      const targetLabel = match ? match.value : 'All';
      setActiveCategory(targetLabel);
    } else {
      setActiveCategory('All');
    }
  }, [location.search, t]);

  /* Auth listener */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Ensure token is fully loaded to prevent Firestore permission denied errors on refresh
          await user.getIdToken();
        } catch (e) {
          console.error("Token error:", e);
        }
      }
      setCurrentUser(user);
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  /* Load products reactively with retry */
  useEffect(() => {
    if (authLoading) return;
    let unsub = null;
    let isMounted = true;
    let retryTimeout = null;

    const connect = () => {
      const q = query(collection(db, 'products'), orderBy('createdAt', 'desc'));
      unsub = onSnapshot(q, (snapshot) => {
        if (!isMounted) return;
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setProducts(data);
        setProductsLoading(false);
      }, (err) => {
        console.error('Products fetch error, retrying:', err);
        if (isMounted) {
          retryTimeout = setTimeout(connect, 1000);
        }
      });
    };

    connect();

    return () => {
      isMounted = false;
      if (unsub) unsub();
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [authLoading]);

  /* Load company accounts reactively with retry */
  useEffect(() => {
    if (authLoading) return;
    let unsub = null;
    let isMounted = true;
    let retryTimeout = null;

    const connect = () => {
      const q = query(collection(db, 'users'), where('role', '==', 'company'));
      unsub = onSnapshot(q, (snapshot) => {
        if (!isMounted) return;
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setCompanies(data);
        setCompaniesLoading(false);
      }, (err) => {
        console.error('Companies fetch error, retrying:', err);
        if (isMounted) {
          retryTimeout = setTimeout(connect, 1000);
        }
      });
    };

    connect();

    return () => {
      isMounted = false;
      if (unsub) unsub();
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [authLoading]);

  /* Reset pagination on filter changes */
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, searchQuery]);

  /* Scroll-reveal observer */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            obs.unobserve(entry.target);
          }
        });
      },
      { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    const els = document.querySelectorAll(
      '.reveal, .reveal-left, .reveal-right, .reveal-scale, .reveal-stagger'
    );
    els.forEach((el) => observer.observe(el));
    return () => els.forEach((el) => observer.unobserve(el));
  }, [loading, productsLoading, products, orders, companies, companiesLoading]);

  /* Load orders for this user */
  useEffect(() => {
    if (authLoading) return;  // 👈 inside, not outside

    if (!currentUser) {
      setOrders([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'orders'),
      where('customerId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(q, (snap) => {
      setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.error('Orders fetch error:', err);
      setLoading(false);
    });

    return () => unsub();
  }, [currentUser, authLoading]);

  /* Filter orders */
  const filtered = orders.filter((o) => {
    const matchesCat =
      activeCategory === 'All' ||
      (o.category || '').toLowerCase().includes(activeCategory.toLowerCase());
    const matchesSearch =
      !searchQuery ||
      (o.productName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (o.companyName || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCat && matchesSearch;
  });

  /* Filter products */
  const filteredProducts = products.filter((p) => {
    const matchesCat =
      activeCategory === 'All' ||
      (p.category || '').toLowerCase().includes(activeCategory.toLowerCase());
    const matchesSearch =
      !searchQuery ||
      (p.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.posterDisplayName || p.posterEmail || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCat && matchesSearch;
  });

  /* Pagination math for products */
  const itemsPerPage = 9;
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + itemsPerPage);


  return (
    <div className="orders-page">
      <Navbar />

      {/* ── HERO ── */}
      <div className={`orders-hero ${isLoaded ? 'animate-in' : ''}`}>
        <h1>{t('orders.heroTitleLine1')}<br />{t('orders.heroTitleLine2')}</h1>
        <p>
          {t('orders.heroDesc')}
        </p>
        <div className="orders-searchbar">
          <FaLocationDot />
          <input
            type="text"
            placeholder={t('orders.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button>
            {t('orders.searchBtn')} <FaArrowRight />
          </button>
        </div>
      </div>

      {/* ── COMPANIES SECTION ── */}
      <div className="orders-companies-section reveal">
        <div className="orders-companies-header">
          <h2>{t('orders.ourPartners')}</h2>
          <p>{t('orders.ourPartnersDesc')}</p>
        </div>
        {companiesLoading ? (
          <div className="orders-state-box">
            <div className="orders-spinner" />
            <p>{t('orders.loading')}</p>
          </div>
        ) : companies.length === 0 ? (
          <div className="orders-state-box">
            <FaBoxOpen className="orders-state-icon" />
            <p>{t('orders.noPartnersYet')}</p>
          </div>
        ) : (
          <div className="orders-companies-grid reveal-stagger">
            {companies.map((company) => {
              const name = company.businessName || `${company.firstName || ''} ${company.lastName || ''}`.trim() || company.email?.split('@')[0] || t('card.unknown');
              const description = company.description || company.businessLocation || "Premium CraveDash culinary partner.";
              const pseudoProduct = {
                id: company.id,
                name: name,
                description: description,
                images: company.photoURL ? [company.photoURL] : [],
                category: "Partner",
                finalPrice: null,
                posterDisplayName: name,
                posterEmail: company.email,
              };

              return (
                <Card
                  key={company.id}
                  product={pseudoProduct}
                  isCompany={true}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* ── CATEGORIES ── */}
      <div className="orders-categories">
        <div className="orders-categories-header reveal">
          <div>
            <h2>{t('orders.cravingSpecific')}</h2>
            <p>{t('orders.filterCategory')}</p>
          </div>
        </div>
        <div className="orders-category-row reveal-stagger">
          {CATEGORIES.map(({ label, value, icon }) => (
            <CategoryPill
              key={value}
              icon={icon}
              label={label}
              active={activeCategory === value}
              onClick={() => setActiveCategory(value)}
            />
          ))}
        </div>
      </div>

      {/* ── TRENDING NOW (products list) ── */}
      <div className="orders-trending-section">
        <div className="orders-trending-header reveal">
          <div className="orders-trending-header-left">
            <h2>{t('orders.trendingNow')}</h2>
            <p>{t('orders.popularChoices')}</p>
          </div>
          {totalPages > 1 && (
            <div className="orders-trending-pagination">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="orders-pagination-btn"
              >
                <FaChevronLeft />
              </button>
              <span className="orders-pagination-info">
                {t('orders.page')} {currentPage} {t('orders.of')} {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="orders-pagination-btn"
              >
                <FaChevronRight />
              </button>
            </div>
          )}
        </div>

        {productsLoading ? (
          <div className="orders-state-box">
            <div className="orders-spinner" />
            <p>{t('orders.loadingProducts')}</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="orders-state-box">
            <FaBoxOpen className="orders-state-icon" />
            <p>{t('orders.noProducts')}</p>
          </div>
        ) : (
          <div className="orders-products-grid reveal-stagger">
            {paginatedProducts.map((product) => (
              <Card
                key={product.id}
                product={product}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── YOUR RECENT ORDERS (orders feed list) ── */}
      <div className="orders-feed-section">
        <div className="orders-feed-header reveal">
          <div>
            <h2>
              {currentUser
                ? filtered.length > 0
                  ? t('orders.yourRecentOrders')
                  : t('orders.noOrdersFound')
                : t('orders.signInToView')}
            </h2>
            <p>
              {currentUser
                ? `${filtered.length} ${filtered.length === 1 ? t('orders.orderFound') : t('orders.ordersFound')}`
                : t('orders.historyAppearHere')}
            </p>
          </div>
          <Link to="/products" className="orders-see-all">
            {t('orders.browseMenu')} <FaChevronRight />
          </Link>
        </div>

        {loading ? (
          <div className="orders-state-box">
            <div className="orders-spinner" />
            <p>{t('orders.loadingOrders')}</p>
          </div>
        ) : authLoading ? (
          <div className="orders-state-box">
            <div className="orders-spinner" />
            <p>{t('orders.loading')}</p>
          </div>
        ) : !currentUser ? (
          <div className="orders-state-box">
            <FaBoxOpen className="orders-state-icon" />
            <p>{t('orders.pleaseSignIn')} <Link to="/login" className="orders-link">{t('orders.signInLink')}</Link></p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="orders-state-box">
            <FaBoxOpen className="orders-state-icon" />
            <p>{t('orders.noOrdersYet')} <Link to="/products" className="orders-link">{t('orders.startOrderingLink')}</Link></p>
          </div>
        ) : (
          <div className="orders-card-grid reveal-stagger">
            {filtered.map((order) => (
              <OrderCard 
                key={order.id} 
                order={order} 
                onLeaveReview={(o) => setReviewingOrder(o)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── CRAVE DASH + PROMO BANNER ── */}
      <div className="orders-promo-banner reveal-scale">
        <div className="orders-promo-inner">
          <span className="orders-promo-tag">{t('orders.craveDashPlus')}</span>
          <h2>{t('orders.get0Delivery')}</h2>
          <p>{t('orders.joinCraveDashPlus')}</p>
          <button className="orders-promo-btn">{t('orders.startFreeTrial')}</button>
        </div>
      </div>

      {reviewingOrder && (
        <RatingModal 
          order={reviewingOrder} 
          currentUser={currentUser} 
          onClose={() => setReviewingOrder(null)} 
        />
      )}

      <Footer />
    </div>
  );
}